/**
 * The app store (architecture §3.3): one `SaveState`, a signal per slice, copy-on-write `update()`, batched
 * `transaction()`, `replace()` for load / fixtures / import, and a store log (`__marg.getState().storeLog`, last 100).
 *
 * Only the owning feature calls `update()` on its slice (tests/architecture/purity.test.ts). The store itself never
 * touches storage: `onCommit` lets the app schedule a save.
 */
import { batch, signal, type ReadonlySignal, type SaveState, type Signal, type SliceKey, type Store, SLICE_KEYS } from '../shared';

export interface StoreOptions {
  /** Wall clock for `updatedAt` (injected: the store stays testable). */
  nowWall: () => number;
  /** Called once per committed change (one per transaction) — the app schedules a save here. */
  onCommit?: (state: SaveState, reason: string) => void;
  /** Store log length (default 100). */
  logSize?: number;
}

type Slices = SaveState['slices'];

export class AppStore implements Store {
  private cur: SaveState;
  private readonly signals: { [K in SliceKey]: Signal<Slices[K]> };
  private readonly subs = new Set<(state: SaveState, reason: string) => void>();
  private readonly logLines: string[] = [];
  private txDepth = 0;
  private txReasons: string[] = [];
  private txDirty = false;

  constructor(initial: SaveState, private readonly opts: StoreOptions) {
    this.cur = initial;
    const sig = {} as { [K in SliceKey]: Signal<Slices[K]> };
    for (const k of SLICE_KEYS) (sig as Record<SliceKey, Signal<unknown>>)[k] = signal<unknown>(initial.slices[k]);
    this.signals = sig;
  }

  get state(): Readonly<SaveState> { return this.cur; }

  slice<K extends SliceKey>(key: K): ReadonlySignal<Slices[K]> { return this.signals[key]; }

  update<K extends SliceKey>(key: K, recipe: (draft: Slices[K]) => void, reason: string): void {
    if (this.txDepth === 0) {
      this.transaction(reason, () => this.update(key, recipe, reason));
      return;
    }
    const draft = structuredClone(this.cur.slices[key]);
    recipe(draft);
    this.cur = { ...this.cur, updatedAt: Math.max(this.cur.updatedAt, this.opts.nowWall()), slices: { ...this.cur.slices, [key]: draft } };
    this.txDirty = true;
    this.log(`${key}: ${reason}`);
  }

  transaction(reason: string, fn: () => void): void {
    this.txDepth++;
    this.txReasons.push(reason);
    try {
      fn();
    } finally {
      this.txDepth--;
      if (this.txDepth === 0) {
        const reasons = this.txReasons;
        this.txReasons = [];
        if (this.txDirty) {
          this.txDirty = false;
          this.publish(reasons[0] ?? reason);
        }
      }
    }
  }

  replace(next: SaveState, reason: string): void {
    this.cur = next;
    this.log(`replace: ${reason}`);
    if (this.txDepth > 0) { this.txDirty = true; return; }
    this.publish(`replace: ${reason}`);
  }

  subscribe(fn: (state: SaveState, reason: string) => void): () => void {
    this.subs.add(fn);
    return () => { this.subs.delete(fn); };
  }

  /** The last `logSize` reasons, oldest first. */
  log(line?: string): string[] {
    if (line !== undefined) {
      this.logLines.push(line);
      const max = this.opts.logSize ?? 100;
      if (this.logLines.length > max) this.logLines.splice(0, this.logLines.length - max);
    }
    return this.logLines.slice();
  }

  /** Touches `lastActiveAt` (max-monotonic: a clock rollback never moves it back). Not logged, no notification. */
  touchActive(nowWall: number): void {
    if (nowWall > this.cur.lastActiveAt) this.cur = { ...this.cur, lastActiveAt: nowWall };
  }

  private publish(reason: string): void {
    const state = this.cur;
    batch(() => {
      for (const k of SLICE_KEYS) (this.signals[k] as Signal<unknown>).set(state.slices[k]);
    });
    for (const fn of [...this.subs]) {
      try { fn(state, reason); } catch (e) { console.error('[store] subscriber threw', e); }
    }
    this.opts.onCommit?.(state, reason);
  }
}
